import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { InputNumber } from "primereact/inputnumber";
import { Tooltip } from "primereact/tooltip";
import SimpleModal from "./SimpleModal";
import PropTypes from "prop-types";
import { router } from "../../data/Services/ordersRouter";
import marketdata from "../../data/Services/marketdata";
import { getOtoTypeLabel } from "../../utils/orderConversions";
import { validateOrderReplacement } from "../../utils/orderReplacementService";

const QuantityEditModal = ({
  visible,
  onHide,
  onConfirm,
  initialQuantity,
  initialPrice,
  orderType,
  symbol,
  title = "Editar Ordem",
  confirmLabel = "Salvar",
  cancelLabel = "Cancelar",
  initialOrder = null, // Add support for full order object with OTO data
}) => {
  const [quantity, setQuantity] = useState(initialQuantity || 0);
  const [price, setPrice] = useState(initialPrice || 0);
  const [hasValidationError, setHasValidationError] = useState(false);

  // Track if we've already focused the input to prevent repeated focus on re-renders
  // This fixes tablet keyboard loop issue where autoFocus triggers resize → remount → autoFocus
  const hasFocusedRef = useRef(false);

  // OTO state management
  const [otoState, setOtoState] = useState({
    hasOTO: false,
    stopPrice: 0,
    profitPrice: 0,
    stopId: null,
    profitId: null,
  });

  // Store initial relative distances for OTO recalculation
  // Note: Removed otoRelativeDistances state - no longer doing automatic OTO price adjustments

  // Detect if order has OTO legs and initialize OTO state
  useEffect(() => {
    if (initialOrder && initialOrder.oto && initialOrder.oto.length > 0) {
      const stopLeg = initialOrder.oto.find(
        (leg) =>
          leg.type === "stop" || (leg.orderType === "STOPLIMIT" && !leg.type),
      );
      const profitLeg = initialOrder.oto.find(
        (leg) =>
          leg.type === "profit" || (leg.orderType === "LIMIT" && !leg.type),
      );

      setOtoState({
        hasOTO: true,
        stopPrice: stopLeg ? stopLeg.price : 0,
        profitPrice: profitLeg ? profitLeg.price : 0,
        stopId: stopLeg ? stopLeg.id : null,
        profitId: profitLeg ? profitLeg.id : null,
      });
    } else {
      setOtoState({
        hasOTO: false,
        stopPrice: 0,
        profitPrice: 0,
        stopId: null,
        profitId: null,
      });
    }
  }, [initialOrder, initialPrice]);

  // Update quantity and price when initial values change
  useEffect(() => {
    setQuantity(initialQuantity || 0);
    setPrice(initialPrice || 0);
    setHasValidationError(false);
  }, [initialQuantity, initialPrice]);

  // Handle quantity changes - don't auto-adjust, just validate
  const handleQuantityChange = (value) => {
    setQuantity(value || 0);

    if (!value || !symbol) {
      setHasValidationError(!value || value <= 0);
      return;
    }

    // Validate if the value is valid for the symbol
    const standardLot = router.adjustStandardLot(symbol, 1);
    const quantityValid =
      value > 0 && value >= standardLot && value % standardLot === 0;
    const priceValid = isPriceValid();

    setHasValidationError(!quantityValid || !priceValid);
  };

  // Handle price changes
  const handlePriceChange = (value) => {
    const newPrice = value || 0;
    setPrice(newPrice);

    // Note: Removed automatic OTO price adjustment - users should manually adjust OTO prices
    // when they change the main price. This prevents unwanted automatic adjustments.

    // Validate both quantity and price
    const quantityValid = isQuantityValid();
    const priceValid = orderType === "MARKET" || (newPrice && newPrice > 0);
    setHasValidationError(!quantityValid || !priceValid);
  };

  // Handle stop price changes
  const handleStopPriceChange = (value) => {
    const newStopPrice = value || 0;
    setOtoState((prev) => ({ ...prev, stopPrice: newStopPrice }));
  };

  // Handle profit price changes
  const handleProfitPriceChange = (value) => {
    const newProfitPrice = value || 0;
    setOtoState((prev) => ({ ...prev, profitPrice: newProfitPrice }));
  };

  // Memoized price step calculation to avoid recalculating on every render
  const priceStep = useMemo(() => {
    if (!symbol) return 0.01; // Default step

    try {
      const minIncrement = Number(marketdata.getMinInc(symbol));
      return isNaN(minIncrement) || minIncrement <= 0 ? 0.01 : minIncrement;
    } catch (error) {
      console.warn("❌ Error getting price step for", symbol, ":", error);
      return 0.01; // Fallback
    }
  }, [symbol]);

  // Memoized decimal places calculation
  const priceDecimals = useMemo(() => {
    if (priceStep >= 1) return 0;
    if (priceStep >= 0.1) return 1;
    if (priceStep >= 0.01) return 2;
    if (priceStep >= 0.001) return 3;
    return 4; // For very small increments
  }, [priceStep]);

  // Workaround for PrimeReact InputNumber bug: addWithPrecision uses hardcoded
  // precision=10 which only supports 1 decimal place. For step=0.01 we need 100.
  const precisionFactor = useMemo(() => {
    if (priceStep >= 1) return 1;
    if (priceStep >= 0.1) return 10;
    if (priceStep >= 0.01) return 100;
    if (priceStep >= 0.001) return 1000;
    return 10000;
  }, [priceStep]);

  const spinPrice = useCallback(
    (currentPrice, dir) => {
      const newPrice =
        Math.round((currentPrice + priceStep * dir) * precisionFactor) /
        precisionFactor;
      return Math.max(0.01, Math.min(newPrice, 999999));
    },
    [priceStep, precisionFactor],
  );

  // Validate quantity before allowing actions
  const isQuantityValid = () => {
    if (!symbol) return quantity > 0;

    const standardLot = router.adjustStandardLot(symbol, 1);
    return (
      quantity > 0 && quantity >= standardLot && quantity % standardLot === 0
    );
  };

  // Validate price
  const isPriceValid = () => {
    // For market orders, price validation is not needed
    if (orderType === "MARKET") return true;

    // For limit/stop orders, price must be positive
    if (price <= 0) return false;

    // Check if price is a multiple of minimum increment
    if (symbol) {
      const remainder = price % priceStep;
      const isMultiple =
        Math.abs(remainder) < 0.0001 ||
        Math.abs(remainder - priceStep) < 0.0001;
      return isMultiple;
    }

    return true;
  };

  // Overall validation
  const isFormValid = () => {
    const basicValid = isQuantityValid() && isPriceValid();
    const otoValid = isOTOValid();
    return basicValid && otoValid;
  };

  // Validate OTO prices relative to main order
  const isOTOValid = () => {
    if (!otoState.hasOTO) return true; // No OTO to validate

    const mainPrice = price;
    const isBuyOrder = initialOrder?.side === "buy";

    let isValid = true;
    let invalidReasons = [];

    // Validate stop price (should be unfavorable to main order)
    if (otoState.stopId && otoState.stopPrice > 0) {
      if (isBuyOrder) {
        // For buy orders, stop should be below main price
        if (otoState.stopPrice >= mainPrice) {
          isValid = false;
          invalidReasons.push(
            `Stop loss (${otoState.stopPrice}) deve ser menor que preço principal (${mainPrice})`,
          );
        }
      } else {
        // For sell orders, stop should be above main price
        if (otoState.stopPrice <= mainPrice) {
          isValid = false;
          invalidReasons.push(
            `Stop loss (${otoState.stopPrice}) deve ser maior que preço principal (${mainPrice})`,
          );
        }
      }
    }

    // Validate profit price (should be favorable to main order)
    if (otoState.profitId && otoState.profitPrice > 0) {
      if (isBuyOrder) {
        // For buy orders, profit should be above main price
        if (otoState.profitPrice <= mainPrice) {
          isValid = false;
          invalidReasons.push(
            `Take profit (${otoState.profitPrice}) deve ser maior que preço principal (${mainPrice})`,
          );
        }
      } else {
        // For sell orders, profit should be below main price
        if (otoState.profitPrice >= mainPrice) {
          isValid = false;
          invalidReasons.push(
            `Take profit (${otoState.profitPrice}) deve ser menor que preço principal (${mainPrice})`,
          );
        }
      }
    }

    return isValid;
  };

  // Handle quantity submission (used by both form submit and confirm button)
  const handleSubmit = () => {
    if (isFormValid()) {
      // Prepare submission data including OTO prices if they exist
      const submissionData = {
        quantity,
        price,
        oto: otoState.hasOTO
          ? {
              stopPrice: otoState.stopPrice,
              profitPrice: otoState.profitPrice,
              stopId: otoState.stopId,
              profitId: otoState.profitId,
            }
          : null,
      };

      // Check if there are actual changes before submitting using centralized service
      const validation = validateOrderReplacement({
        dragStart: null, // Modal operations don't need drag validation
        dragEnd: null,
        originalOrder: initialOrder,
        newValues: submissionData,
        operationType: "modal",
      });

      try {
        if (validation.hasChanges) {
          onConfirm(quantity, price, submissionData.oto);
        } else {
          // Always call onConfirm - let the parent decide if changes are meaningful
          onConfirm(quantity, price, submissionData.oto);
        }
      } catch (error) {
        console.error(
          "❌ Error in onConfirm, but closing modal anyway:",
          error,
        );
      }

      onHide();
    } else {
      setHasValidationError(true);
      console.error("❌ Invalid order values - validation details:", {
        quantity,
        price,
        orderType,
        quantityValid: isQuantityValid(),
        priceValid: isPriceValid(),
        otoValid: isOTOValid(),
      });
    }
  };

  const handleConfirm = () => {
    handleSubmit();
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    handleSubmit();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Handle modal closing (used by cancel button and X button)
  const handleModalHide = () => {
    // Reset focus tracking so next open will focus again
    hasFocusedRef.current = false;

    // Always allow modal to close - don't enforce validation on cancel
    onHide();

    setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
    }, 100); // Small delay to ensure modal is fully closed
  };

  const handleModalShow = () => {
    // Only focus if we haven't already focused in this modal session
    // This prevents tablet keyboard loop: autoFocus → keyboard → resize → remount → autoFocus
    if (hasFocusedRef.current) {
      return;
    }
    hasFocusedRef.current = true;

    // Focus the input when modal is shown
    setTimeout(() => {
      // Try to find the input element inside PrimeReact InputNumber
      const inputElement = document.querySelector(
        ".quantity-edit-modal .p-inputnumber-input",
      );
      if (inputElement) {
        inputElement.focus();

        // Try to select the text if methods are available
        try {
          if (typeof inputElement.select === "function") {
            inputElement.select();
          } else if (
            typeof inputElement.setSelectionRange === "function" &&
            inputElement.value
          ) {
            inputElement.setSelectionRange(0, inputElement.value.length);
          }
        } catch (error) {
          console.warn("Could not select text:", error);
        }
      }
    }, 50); // Longer delay for PrimeReact to render
  };

  return (
    <>
      <Tooltip target=".quantity-edit-input" />
      <Tooltip target=".price-edit-input" />
      <Tooltip target=".stop-price-edit-input" />
      <Tooltip target=".profit-price-edit-input" />

      <SimpleModal
        title={title}
        visible={visible}
        onHide={handleModalHide}
        onConfirm={handleConfirm}
        confirmLabel={confirmLabel}
        cancelLabel={cancelLabel}
        size="tiny"
        onShow={handleModalShow}
        closable={true}
      >
        <div className="quantity-edit-modal">
          <form onSubmit={handleFormSubmit}>
            <div className="p-field">
              <label htmlFor="quantity-input" className="p-d-block">
                Quantidade:
                {symbol && (
                  <span className="standard-lot-info">
                    (Lote padrão: {router.adjustStandardLot(symbol, 1)})
                  </span>
                )}
              </label>
              <InputNumber
                id="quantity-input"
                value={quantity}
                onValueChange={(e) => handleQuantityChange(e.value)}
                onKeyDown={handleKeyDown}
                min={symbol ? router.adjustStandardLot(symbol, 1) : 1}
                max={999999}
                maxFractionDigits={0}
                minFractionDigits={0}
                step={symbol ? router.adjustStandardLot(symbol, 1) : 1}
                useGrouping={false}
                showButtons
                buttonLayout="horizontal"
                incrementButtonIcon="pi pi-plus"
                decrementButtonIcon="pi pi-minus"
                className={`quantity-input quantity-edit-input ${hasValidationError ? "p-invalid" : ""}`}
                data-pr-tooltip={
                  symbol
                    ? `Quantidade deve ser múltiplo do lote padrão (${router.adjustStandardLot(symbol, 1)})`
                    : "Selecione um símbolo para ver as restrições de lote"
                }
                data-pr-position="top"
                data-evo-id="quantityeditmodal-input-quantity"
              />
              {hasValidationError && !isQuantityValid() && (
                <small className="p-error">
                  Quantidade deve ser múltiplo de{" "}
                  {symbol ? router.adjustStandardLot(symbol, 1) : 1}.
                </small>
              )}
            </div>

            {/* Price field - only show for LIMIT and STOPLIMIT orders */}
            {orderType && orderType !== "MARKET" && (
              <div className="p-field" style={{ marginTop: "16px" }}>
                <label htmlFor="price-input" className="p-d-block">
                  Preço{" "}
                  {orderType === "LIMIT"
                    ? "Limitado"
                    : orderType === "STOPLIMIT"
                      ? "Stop"
                      : ""}
                  :
                </label>
                <div className="p-inputgroup">
                  <button
                    type="button"
                    className="p-inputgroup-addon p-button p-component p-button-icon-only"
                    onClick={() => handlePriceChange(spinPrice(price, -1))}
                    data-evo-id="quantityeditmodal-btn-price-decrement"
                  >
                    <i className="pi pi-minus" />
                  </button>
                  <InputNumber
                    id="price-input"
                    value={price}
                    onValueChange={(e) => handlePriceChange(e.value)}
                    onKeyDown={handleKeyDown}
                    min={0.01}
                    max={999999}
                    maxFractionDigits={priceDecimals}
                    minFractionDigits={priceDecimals}
                    className={`price-input price-edit-input ${hasValidationError && !isPriceValid() ? "p-invalid" : ""}`}
                    data-pr-tooltip={`Preço deve ser múltiplo de ${priceStep} (incremento mínimo para ${symbol})`}
                    data-pr-position="top"
                    data-evo-id="quantityeditmodal-input-price"
                  />
                  <button
                    type="button"
                    className="p-inputgroup-addon p-button p-component p-button-icon-only"
                    onClick={() => handlePriceChange(spinPrice(price, 1))}
                    data-evo-id="quantityeditmodal-btn-price-increment"
                  >
                    <i className="pi pi-plus" />
                  </button>
                </div>
                {hasValidationError && !isPriceValid() && (
                  <small className="p-error">
                    Preço deve ser múltiplo de {priceStep} (incremento mínimo
                    para {symbol}).
                  </small>
                )}
              </div>
            )}

            {/* OTO fields - show when order has OTO legs */}
            {otoState.hasOTO && (
              <div
                style={{
                  marginTop: "20px",
                  borderTop: "1px solid #ddd",
                  paddingTop: "16px",
                }}
              >
                <div
                  style={{
                    marginBottom: "12px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#333",
                  }}
                >
                  Ordens OTO (Stop/Profit)
                </div>

                {/* Stop (Perda) Price */}
                {otoState.stopId && (
                  <div className="p-field" style={{ marginBottom: "12px" }}>
                    <label htmlFor="stop-price-input" className="p-d-block">
                      {getOtoTypeLabel("stop")} (Stop Loss):
                    </label>
                    <div className="p-inputgroup">
                      <button
                        type="button"
                        className="p-inputgroup-addon p-button p-component p-button-icon-only"
                        onClick={() =>
                          handleStopPriceChange(
                            spinPrice(otoState.stopPrice, -1),
                          )
                        }
                        data-evo-id="quantityeditmodal-btn-stop-price-decrement"
                      >
                        <i className="pi pi-minus" />
                      </button>
                      <InputNumber
                        id="stop-price-input"
                        value={otoState.stopPrice}
                        onValueChange={(e) => handleStopPriceChange(e.value)}
                        onKeyDown={handleKeyDown}
                        min={0.01}
                        max={999999}
                        maxFractionDigits={priceDecimals}
                        minFractionDigits={priceDecimals}
                        className={`stop-price-input stop-price-edit-input ${hasValidationError && !isOTOValid() ? "p-invalid" : ""}`}
                        data-pr-tooltip={`Preço de stop loss - deve ser múltiplo de ${priceStep}`}
                        data-pr-position="top"
                        data-evo-id="quantityeditmodal-input-stop-price"
                      />
                      <button
                        type="button"
                        className="p-inputgroup-addon p-button p-component p-button-icon-only"
                        onClick={() =>
                          handleStopPriceChange(
                            spinPrice(otoState.stopPrice, 1),
                          )
                        }
                        data-evo-id="quantityeditmodal-btn-stop-price-increment"
                      >
                        <i className="pi pi-plus" />
                      </button>
                    </div>
                    {hasValidationError && !isOTOValid() && (
                      <small className="p-error">
                        Stop deve ser{" "}
                        {initialOrder?.side === "buy" ? "menor" : "maior"} que o
                        preço principal.
                      </small>
                    )}
                  </div>
                )}

                {/* Profit (Ganho) Price */}
                {otoState.profitId && (
                  <div className="p-field">
                    <label htmlFor="profit-price-input" className="p-d-block">
                      {getOtoTypeLabel("profit")} (Take Profit):
                    </label>
                    <div className="p-inputgroup">
                      <button
                        type="button"
                        className="p-inputgroup-addon p-button p-component p-button-icon-only"
                        onClick={() =>
                          handleProfitPriceChange(
                            spinPrice(otoState.profitPrice, -1),
                          )
                        }
                        data-evo-id="quantityeditmodal-btn-profit-price-decrement"
                      >
                        <i className="pi pi-minus" />
                      </button>
                      <InputNumber
                        id="profit-price-input"
                        value={otoState.profitPrice}
                        onValueChange={(e) => handleProfitPriceChange(e.value)}
                        onKeyDown={handleKeyDown}
                        min={0.01}
                        max={999999}
                        maxFractionDigits={priceDecimals}
                        minFractionDigits={priceDecimals}
                        className={`profit-price-input profit-price-edit-input ${hasValidationError && !isOTOValid() ? "p-invalid" : ""}`}
                        data-pr-tooltip={`Preço de take profit - deve ser múltiplo de ${priceStep}`}
                        data-pr-position="top"
                        data-evo-id="quantityeditmodal-input-profit-price"
                      />
                      <button
                        type="button"
                        className="p-inputgroup-addon p-button p-component p-button-icon-only"
                        onClick={() =>
                          handleProfitPriceChange(
                            spinPrice(otoState.profitPrice, 1),
                          )
                        }
                        data-evo-id="quantityeditmodal-btn-profit-price-increment"
                      >
                        <i className="pi pi-plus" />
                      </button>
                    </div>
                    {hasValidationError && !isOTOValid() && (
                      <small className="p-error">
                        Ganho deve ser{" "}
                        {initialOrder?.side === "buy" ? "maior" : "menor"} que o
                        preço principal.
                      </small>
                    )}
                  </div>
                )}
              </div>
            )}
          </form>
        </div>
      </SimpleModal>
    </>
  );
};

QuantityEditModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  initialQuantity: PropTypes.number,
  initialPrice: PropTypes.number,
  orderType: PropTypes.string,
  symbol: PropTypes.string,
  title: PropTypes.string,
  confirmLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  initialOrder: PropTypes.object, // Full order object with potential OTO data
};

export default QuantityEditModal;
